import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import './Login.css';
import Alert from '@material-ui/lab/Alert';
import Seo from '../../components/global/Seo';

// redux
import {
  isAuthenticatedSelector,
  loginCaptchaRequiredSelector,
  showLicenseDialogSelector,
  tokenSelector,
  userInfoSelector,
} from '../../redux/selectors/auth';
import { authLogin } from '../../redux/actions/auth';
import withShipment from '../../withShipment';
import ErrorFeedbackBar from '../../components/global/ErrorFeedbackBar';
import { activateLicenseKey } from '../../redux/actions/settings';
import {
  hideLicenseDialog,
  showLicenseDialog,
} from '../../redux/actions/global';
import { InputAdornment, IconButton } from '@material-ui/core';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
} from '@material-ui/icons';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Button,
  Typography,
  Box,
} from '@material-ui/core';
import PayWithStripeDialog from '../../components/stripe/PayWithStripeDialog';
import {
  loadRecaptcha,
  renderCheckbox,
  getCheckboxResponse,
  resetCheckbox,
} from '../../components/Utils/recaptcha';

class Login extends Component {
  state = {
    username: '',
    password: '',
    display: 'none',
    loading: false,
    key: '',
    showPassword: false,
    // Phase 2 — Stripe Pay & Activate flow on the expired dialog.
    payDialogOpen: false,
  };

  // Container for the visible reCAPTCHA v2 checkbox (only mounted after
  // repeated failed logins).
  captchaRef = React.createRef();

  componentDidMount() {
    this.mounted = true;
    // Warm up the invisible reCAPTCHA script so the first login submit can
    // mint a token without an extra round-trip delay. No-op without a site key.
    loadRecaptcha();

    // If the captcha requirement survived a refresh (persisted flag rehydrated
    // as true), the false -> true transition in componentDidUpdate never fires,
    // so render the checkbox here. Without this, a refreshed page would show an
    // empty captcha area and the user could never produce a token. renderCheckbox
    // awaits the script load internally, so calling it before loadRecaptcha
    // resolves is safe.
    if (this.props.loginCaptchaRequired) {
      renderCheckbox(this.captchaRef.current);
    }
  }
  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    // The backend just flagged that a captcha is needed — render the v2
    // checkbox now that its container is in the DOM.
    if (!prevProps.loginCaptchaRequired && this.props.loginCaptchaRequired) {
      renderCheckbox(this.captchaRef.current);
    }

    // Trigger save prompt the moment login succeeds
    if (!prevProps.isAuthenticated && this.props.isAuthenticated) {
      const { username, password } = this.state;
      if (window.PasswordCredential && username && password) {
        try {
          const cred = new window.PasswordCredential({
            id: username,
            password: password,
            name: username,
          });
          navigator.credentials
            .store(cred)
            .catch((e) => console.warn('Credential store failed:', e));
        } catch (e) {
          console.warn('PasswordCredential error:', e);
        }
      }
    }
  }

  handle = (event) => {
    event.preventDefault();
    const state = { ...this.state };
    state[event.target.name] = event.target.value;
    this.setState(state);
  };

  handleClickShowPassword = () => {
    this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
  };

  handleLogin = async (event) => {
    event.preventDefault();
    const { username, password } = this.state;

    if (!username || !password) {
      this.setState({ display: '' });
      return;
    }

    // Once the captcha is showing, the user must solve it before we submit.
    let captchaToken = null;
    if (this.props.loginCaptchaRequired) {
      captchaToken = getCheckboxResponse();
      if (!captchaToken) {
        // Surface the inline alert prompting them to complete the checkbox.
        this.setState({ display: '' });
        return;
      }
    }

    this.setState({ loading: true, display: 'none' });

    this.props.authLogin(username, password, this.props.history, captchaToken);

    // v2 tokens are single-use — clear the checkbox so the next attempt
    // (if any) requires a fresh solve.
    resetCheckbox();

    setTimeout(async () => {
      if (this.mounted) {
        const loggedIn = this.props.isAuthenticated;

        // If login succeeded, ask iOS/browser to save the credentials
        if (loggedIn && window.PasswordCredential) {
          try {
            const cred = new window.PasswordCredential({
              id: username,
              password: password,
              name: username,
            });
            await navigator.credentials.store(cred);
          } catch (e) {
            console.warn('Credential store failed:', e);
          }
        }

        this.setState({
          loading: false,
          display: loggedIn ? 'none' : '',
        });
      }
    }, 2500);
  };

  // handleLogin = (event) => {
  //   event.preventDefault();
  //   const { username, password } = this.state;
  //
  //   if (!username || !password) {
  //     this.setState({ display: '' });
  //     return;
  //   }
  //
  //   this.setState({ loading: true, display: 'none' });
  //
  //   this.props.authLogin(username, password, this.props.history);
  //
  //   setTimeout(() => {
  //     if (this.mounted) {
  //       this.setState({
  //         loading: false,
  //         display: this.props.isAuthenticated ? 'none' : '',
  //       });
  //     }
  //   }, 2500);
  // };

  // Where an already-signed-in visitor belongs. Mirrors the destinations
  // the login saga pushes to, so arriving here with a live session lands
  // in the same place finishing a login would have.
  authenticatedDestination = () => {
    const roles = this.props.userInfo?.authority || [];
    const isAdmin =
      roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MASTER_ADMIN');
    // Dedicated Security only — Managers and Supervisors carry
    // ROLE_SECURITY alongside ROLE_CUSTOMER and belong on the dashboard.
    const isDedicatedSecurity =
      roles.includes('ROLE_SECURITY') && !roles.includes('ROLE_CUSTOMER');
    return isDedicatedSecurity && !isAdmin ? '/security' : '/dashboard';
  };

  render() {
    // A live session must never be shown the sign-in form. The Flutter
    // wrapper opens the app at /login on every launch, so an employee who
    // killed and reopened it landed on this screen with a perfectly valid
    // token still in localStorage and read it as having been logged out —
    // nothing had expired, /login just never checked. Same for anyone who
    // navigates or bookmarks their way back here mid-shift.
    if (this.props.isAuthenticated) {
      return <Redirect to={this.authenticatedDestination()} />;
    }

    return (
      <div className="login-page">
        <Seo title="Sign In" path="/login" noindex />
        <ErrorFeedbackBar />

        <aside className="login-brand-panel">
          <div className="login-glow" />
          <div className="login-grid" />

          <div className="login-brand-content">
            {/* Logo links back to the public landing page (no other login UI
                change). Anchor keeps it router-free; /landing is a public
                clean-layout route. */}
            <a href="/landing" aria-label="NuVe System home">
              <img
                src={`${process.env.PUBLIC_URL || ''}/nube-data.svg`}
                alt="NuVe System"
                className="login-logo"
                style={{ cursor: 'pointer' }}
              />
            </a>
            <h1 className="login-brand-title">NuVe System</h1>
          </div>
        </aside>

        <main className="login-form-panel">
          <div className="login-form-card">
            <div className="login-form-header">
              <h2 className="login-form-title">Welcome back</h2>
              <p className="login-form-subtitle">
                Sign in to access your dashboard
              </p>
            </div>

            <form
              className="login-form-fields"
              onSubmit={this.handleLogin}
              method="post"
              action="#"
            >
              <TextField
                label="Username"
                variant="outlined"
                name="username"
                fullWidth
                required
                autoComplete="username"
                InputLabelProps={{ shrink: true }}
                value={this.state.username}
                onChange={this.handle}
                inputProps={{
                  autoComplete: 'username',
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                  spellCheck: 'false',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type={this.state.showPassword ? 'text' : 'password'}
                variant="outlined"
                name="password"
                fullWidth
                required
                autoComplete="current-password"
                InputLabelProps={{ shrink: true }}
                value={this.state.password}
                onChange={this.handle}
                inputProps={{
                  autoComplete: 'current-password',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={this.handleClickShowPassword}
                        edge="end"
                        size="small"
                        aria-label="toggle password visibility"
                      >
                        {this.state.showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Alert
                className="login-error"
                style={{ display: this.state.display }}
                severity={this.props.loginCaptchaRequired ? 'warning' : 'error'}
              >
                {this.props.loginCaptchaRequired
                  ? 'Please complete the verification below to continue.'
                  : 'Login failed. Please check your credentials.'}
              </Alert>

              {this.props.loginCaptchaRequired && (
                <div className="login-captcha">
                  <Typography
                    variant="body2"
                    style={{ marginBottom: 8, color: '#64748b' }}
                  >
                    Please verify you&apos;re not a robot to continue.
                  </Typography>
                  <div ref={this.captchaRef} />
                </div>
              )}

              {!this.state.loading ? (
                <Button
                  type="submit"
                  variant="contained"
                  className="login-submit-btn"
                  disableElevation
                >
                  Sign in
                </Button>
              ) : (
                <div className="login-loading">
                  <CircularProgress size={28} />
                </div>
              )}
            </form>

            <div className="login-form-signup">
              <span>New to NuVe System?</span>
              {/* Routes to the public onboarding/get-started page. Anchor keeps
                  it router-free; /get-started is a public clean-layout route. */}
              <a href="/get-started?from=login" className="login-signup-link">
                Get started
              </a>
            </div>

            <div className="login-form-footer">
              <span>Trouble signing in? Contact your administrator.</span>
            </div>
          </div>
        </main>

        <Dialog
          open={this.props.showDialog}
          onClose={this.props.hideLicenseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: { borderRadius: 16, padding: '8px 4px' },
          }}
        >
          <DialogTitle disableTypography>
            <Typography
              variant="h6"
              style={{ color: '#d32f2f', fontWeight: 600 }}
            >
              Company Account Expired
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please enter your new license key to reactivate your account.
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Box mt={2}>
              <TextField
                label="License Key"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={this.state.key || ''}
                onChange={(e) => this.setState({ key: e.target.value })}
              />
            </Box>
          </DialogContent>

          <DialogActions style={{ padding: '16px 24px' }}>
            <Button
              onClick={this.props.hideLicenseDialog}
              style={{ fontWeight: 500 }}
            >
              Cancel
            </Button>
            {/* Phase 2: Pay & Activate route — uses Stripe to charge for
                the next cycle and reactivates the company on webhook
                success. The Activate button stays for the legacy "I
                already paid, here's my key" admin flow. */}
            <Button
              variant="outlined"
              color="primary"
              style={{ fontWeight: 500, marginRight: 8 }}
              disabled={!this.state.key}
              onClick={() => this.setState({ payDialogOpen: true })}
            >
              Pay &amp; Activate
            </Button>
            <Button
              color="primary"
              variant="contained"
              style={{ fontWeight: 500 }}
              onClick={() => {
                const { username, password, key } = this.state;
                this.props.activateLicenseKey({ serialKey: key }, (success) => {
                  if (success === true && username && password) {
                    this.setState({ loading: true, display: 'none' });
                    this.props.authLogin(
                      username,
                      password,
                      this.props.history
                    );
                  }
                });
                this.props.hideLicenseDialog();
              }}
            >
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stripe Pay & Activate. Permitted to anonymous callers — scoped
            server-side by the license key the user typed in. Successful
            payment triggers the webhook → company reactivation → activation
            poll resolves → onSuccess fires → we auto-fill credentials + log
            the user back in. */}
        <PayWithStripeDialog
          open={this.state.payDialogOpen}
          mode="pay_and_activate"
          serialKey={this.state.key}
          requiresActivation
          onClose={() => this.setState({ payDialogOpen: false })}
          onSuccess={({ pending }) => {
            const { username, password } = this.state;
            this.setState({ payDialogOpen: false });
            this.props.hideLicenseDialog();
            // Even when pending=true (webhook hasn't landed) we still
            // attempt login — backend will reject if not yet activated and
            // the user can retry in a few seconds. For the happy path
            // (activated=true) this is a direct seamless login.
            if (username && password) {
              this.setState({ loading: true, display: 'none' });
              this.props.authLogin(username, password, this.props.history);
            }
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticatedSelector(state),
  userInfo: userInfoSelector(state),
  token: tokenSelector(state),
  showDialog: showLicenseDialogSelector(state),
  loginCaptchaRequired: loginCaptchaRequiredSelector(state),
});

const actionCreators = {
  authLogin,
  activateLicenseKey,
  showLicenseDialog,
  hideLicenseDialog,
};

export default withShipment({ mapStateToProps, actionCreators }, Login);
